import {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
  lazy,
  Suspense,
} from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Form,
  Dropdown,
  Nav,
  Offcanvas,
  Button,
  InputGroup,
  Badge,
} from "react-bootstrap";
import { BsFilter, BsPeople, BsEnvelopeArrowDown, BsSearch, BsSortDown } from "react-icons/bs";
import { useFindUsers } from "../../api/queries/useFindUser";
import { User, UserImageUrls } from "../../types/Types";
import { useGetConnectionRequests } from "../../api/queries/useGetConnectionRequests";
import { useGetSentConnectionRequests } from "../../api/queries/useGetSentConnectionsRequests";
import { useGetConnections } from "../../api/queries/useGetConnections";
import { useAppContext } from "../../Context/AppContext";
import { explorerOnboardingQueryKey } from "../../auth/authSession";
import { getMyExplorerOnboarding } from "../../api/explorerOnboardingApi";
import { isLoggedIn } from "../../auth/authStorage";
import { resolveCurrentUserConnectionSource } from "../../utils/connectionUserUtils";
import { isUnpaidExplorerUser } from "../../explorer/isUnpaidExplorerUser";
import { useExplorerConnectionLimit } from "../../hooks/useExplorerConnectionLimit";
import { useExplorerConnectionLimitFeedback } from "../../hooks/useExplorerConnectionLimitFeedback";
import { ExplorerConnectionLimitCounter } from "./ExplorerConnectionLimitCounter";
import { CONNECTIONS_SCROLL_AREA_ID } from "./connectionsScrollArea";
import { BUSINESS_TURNOVER_OPTIONS } from "../../utils/businessTurnover";
import {
  CityOption,
  formatCityCountryLabel,
  loadCityOptions,
  splitCityCountry,
} from "../../utils/cityAsyncSelectOptions";
import { SECTOR_OPTIONS } from "../../utils/sectors";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "react-responsive";
import AsyncSelect from "react-select/async";

const UsersList = lazy(() => import("./UsersList"));
const ConnectionsList = lazy(() => import("./ConnectionList"));
const RequestsList = lazy(() => import("./RequestsList"));

/** Values are sent to the API unchanged; `labelKey` is for `t()` display only. */
const SECTOR_FILTER_OPTIONS = [...SECTOR_OPTIONS].sort((a, b) =>
  a.value.localeCompare(b.value),
);

const TURNOVER_FILTER_OPTIONS = BUSINESS_TURNOVER_OPTIONS.map((opt) => ({
  value: opt.value,
  labelKey: opt.labelKey,
}));

/** OR within each dimension, AND across dimensions — matches /User/All discover filters. */
function userMatchesDiscoverFilters(
  u: User,
  opts: { cities: string[]; sectors: string[]; turnovers: string[] }
): boolean {
  const { cities, sectors, turnovers } = opts;
  if (cities.length > 0) {
    const userLoc = splitCityCountry(u.city, u.country);
    if (!userLoc.city) return false;
    const matched = cities.some((entry) => {
      const filterLoc = splitCityCountry(entry);
      if (
        userLoc.city.toLowerCase() !== filterLoc.city.toLowerCase()
      ) {
        return false;
      }
      if (filterLoc.country && userLoc.country) {
        return (
          userLoc.country.toLowerCase() === filterLoc.country.toLowerCase()
        );
      }
      return true;
    });
    if (!matched) return false;
  }
  if (sectors.length > 0) {
    const s = (u.businessSectorIndustry ?? "").trim();
    if (!s) return false;
    if (!sectors.some((x) => s.toLowerCase() === x.trim().toLowerCase())) return false;
  }
  if (turnovers.length > 0) {
    const t = (u.businessTurnover ?? "").trim();
    if (!t) return false;
    if (!turnovers.some((x) => t.toLowerCase() === x.trim().toLowerCase())) return false;
  }
  return true;
}

/** Pure helper: upgrade message for filter based on membership and filter type. */
function getUpgradeMessageKey(
  membershipType: string,
  filterType: string
): string {
  if (membershipType === "youth") {
    return "connections.upgrade.businessOrExecutive";
  }
  if (membershipType === "business") {
    if (filterType === "turnover" || filterType === "country") {
      return "connections.upgrade.executiveOnly";
    }
  }
  return "";
}

// Styles moved to CSS classes

const BODY_CLASS_MOBILE_SCROLL_LOCK = "connections-mobile-noscroll";

function Connections() {
  const { t } = useTranslation();
  const { objectId, user: currentUser } = useAppContext();
  const userId = objectId ?? "";
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  /** Prevents double page bumps from sentinel + duplicate-page auto-advance. */
  const loadMoreLockRef = useRef(false);
  const isLgUp = useMediaQuery({ minWidth: 992 });

  useEffect(() => {
    window.scrollTo(0, 0);
    scrollAreaRef.current?.scrollTo(0, 0);
  }, []);

  const { canPerformConnectionAction, isExplorer } = useExplorerConnectionLimit();
  const explorerLimitReached = !canPerformConnectionAction;
  const {
    guardConnectionAction,
    handleConnectionLimitError,
    limitModal,
  } = useExplorerConnectionLimitFeedback({ autoShowPaywall: true });

  const { data: explorerMe } = useQuery({
    queryKey: explorerOnboardingQueryKey(objectId),
    queryFn: getMyExplorerOnboarding,
    enabled: isLoggedIn() && !!objectId && isExplorer,
    retry: false,
    staleTime: 60_000,
  });

  const currentConnectionSource = useMemo(
    () =>
      resolveCurrentUserConnectionSource(
        currentUser?.user,
        explorerMe,
        isExplorer
      ),
    [currentUser?.user, explorerMe, isExplorer]
  );

  /* Lock body scroll on mobile so only the inner scroll area scrolls; desktop unchanged */
  useEffect(() => {
    const applyLock = () => {
      if (typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches) {
        document.body.classList.add(BODY_CLASS_MOBILE_SCROLL_LOCK);
      }
    };
    const removeLock = () => document.body.classList.remove(BODY_CLASS_MOBILE_SCROLL_LOCK);
    const handleChange = (e: MediaQueryListEvent) => {
      if (e.matches) applyLock();
      else removeLock();
    };
    applyLock();
    const mql = window.matchMedia("(max-width: 767px)");
    mql.addEventListener("change", handleChange);
    return () => {
      removeLock();
      mql.removeEventListener("change", handleChange);
    };
  }, []);

  /** Search query — same value for input, API, and client filters (no live translation). */
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [sortOption, setSortOption] = useState("connections.sort.recent");
  const [hasMoreUsers, setHasMoreUsers] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  const [fullUsersList, setFullUsersList] = useState<User[]>([]);
  const [fullUsersImages, setFullUsersImages] = useState<UserImageUrls[]>([]);
  /** Last discover page merged into fullUsersList (avoids re-apply / skip loops). */
  const lastMergedDiscoverPageRef = useRef(0);

  // Filter states
  const [appliedFilters, setAppliedFilters] = useState({
    cities: [] as string[],
    sectors: [] as string[],
    turnovers: [] as string[],
  });

  // Filter input states
  const [sectorFilter, setSectorFilter] = useState("");
  const [turnoverFilter, setTurnoverFilter] = useState("");

  const [showFilters, setShowFilters] = useState(false);
  const [activeTab, setActiveTab] = useState<
    "connections" | "requests" | "users"
  >("connections");
  /* ================= MEMBERSHIP HELPERS ================= */

  const membershipType = useMemo(() => {
    const membership = currentUser?.user?.membershipName?.toLowerCase() || "";
    if (membership === "youth membership") return "youth";
    if (membership === "business membership") return "business";
    if (membership === "executive membership") return "executive";
    return "youth";
  }, [currentUser?.user?.membershipName]);
  const canUseCityFilter = membershipType === "business" || membershipType === "executive";
  const canUseSectorFilter = membershipType === "business" || membershipType === "executive";
  const canUseTurnoverFilter = membershipType === "executive";

  /* ================= API ================= */

  const cityApiValues = useMemo(
    () =>
      appliedFilters.cities
        .map((entry) => splitCityCountry(entry).city)
        .filter(Boolean),
    [appliedFilters.cities]
  );

  const {
    data: userResponse,
    refetch: refetchUsers,
    isFetching: isFetchingUsers,
    isError: isUsersError,
  } = useFindUsers({
    pageSize: 50,
    page: currentPage,
    searchTerm: debouncedSearchTerm || undefined,
    discoverableOnly: true,
    cities: canUseCityFilter && cityApiValues.length > 0 ? cityApiValues : undefined,
    sectors: canUseSectorFilter && appliedFilters.sectors.length > 0 ? appliedFilters.sectors : undefined,
    turnovers: canUseTurnoverFilter && appliedFilters.turnovers.length > 0 ? appliedFilters.turnovers : undefined,
  });

  const { data: connectionRequests, refetch: refetchConnectionRequests } =
    useGetConnectionRequests({ userId });

  const { data: sentConnectionRequests, refetch: refetchSentConnectionRequests } =
    useGetSentConnectionRequests({ userId });

  const { data: connections, refetch: refetchConnections } =
    useGetConnections({ userId });

  /* ================= EFFECTS ================= */

  // Reset pagination and clear users list when search or filters change
  useEffect(() => {
    setCurrentPage(1);
    setHasMoreUsers(true);
    lastMergedDiscoverPageRef.current = 0;
    loadMoreLockRef.current = false;
    // Clear the users list when filters change
    setFullUsersList([]);
    setFullUsersImages([]);
  }, [debouncedSearchTerm, appliedFilters]);

  useEffect(() => {
    if (isUsersError) {
      setHasMoreUsers(false);
      return;
    }
    if (!userResponse || isFetchingUsers) return;

    const newUsers = userResponse.users?.data ?? [];
    const newImages = userResponse.userImageUrls ?? [];
    const totalPages = userResponse.users?.totalPages ?? 0;
    const pageNumber = userResponse.users?.pageNumber ?? currentPage;
    // Ignore stale cache from a different page while currentPage has moved on.
    if (pageNumber !== currentPage) return;
    // Already merged this page for the current query.
    if (lastMergedDiscoverPageRef.current === pageNumber) return;

    const apiHasMore = pageNumber < totalPages;
    lastMergedDiscoverPageRef.current = pageNumber;

    if (currentPage === 1) {
      setFullUsersList(newUsers);
      setFullUsersImages(newImages);
      setHasMoreUsers(apiHasMore);
      return;
    }

    const existingIds = new Set(fullUsersList.map((u) => u.id));
    const uniqueNewUsers = newUsers.filter((u) => !existingIds.has(u.id));

    if (uniqueNewUsers.length === 0) {
      setHasMoreUsers(apiHasMore);
      // Duplicate/empty API page — keep paging (same lock as scroll sentinel).
      if (apiHasMore && !loadMoreLockRef.current) {
        loadMoreLockRef.current = true;
        setCurrentPage((p) => p + 1);
      }
      return;
    }

    setFullUsersList((prev) => [...prev, ...uniqueNewUsers]);
    setFullUsersImages((prev) => {
      const existingImageIds = new Set(prev.map((img) => img.userId));
      const uniqueNewImages = newImages.filter(
        (img) => !existingImageIds.has(img.userId)
      );
      return uniqueNewImages.length ? [...prev, ...uniqueNewImages] : prev;
    });
    setHasMoreUsers(apiHasMore);
  }, [
    userResponse,
    currentPage,
    isFetchingUsers,
    isUsersError,
    fullUsersList,
  ]);

  // Refetch list when switching tabs
  useEffect(() => {
    if (activeTab === "connections") refetchConnections();
    if (activeTab === "requests") refetchConnectionRequests();
    if (activeTab === "users") refetchUsers();
  }, [activeTab, refetchConnections, refetchConnectionRequests, refetchUsers]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchQuery);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchQuery]);

  /* ================= FILTER FUNCTIONS ================= */

  const addFilter = useCallback(
    (key: keyof typeof appliedFilters, value: string) => {
      if (!value.trim()) return;
      setAppliedFilters((prev) => {
        const newFilters = { ...prev };
        if (!newFilters[key].includes(value)) {
          newFilters[key] = [...newFilters[key], value];
        }
        return newFilters;
      });
    },
    []
  );

  const removeFilter = useCallback(
    (key: keyof typeof appliedFilters, index: number) => {
      setAppliedFilters((prev) => ({
        ...prev,
        [key]: prev[key].filter((_, i) => i !== index),
      }));
    },
    []
  );

  const clearAllFilters = useCallback(() => {
    setAppliedFilters({
      cities: [],
      sectors: [],
      turnovers: [],
    });
  }, []);

  const labelForSectorValue = useCallback(
    (value: string) => {
      const opt = SECTOR_FILTER_OPTIONS.find((o) => o.value === value);
      return opt ? t(opt.labelKey) : value;
    },
    [t]
  );

  const labelForTurnoverValue = useCallback(
    (value: string) => {
      const opt = TURNOVER_FILTER_OPTIONS.find((o) => o.value === value);
      return opt ? t(opt.labelKey) : value;
    },
    [t]
  );

  const hasActiveFilters = useMemo(
    () =>
      appliedFilters.cities.length > 0 ||
      appliedFilters.sectors.length > 0 ||
      appliedFilters.turnovers.length > 0,
    [
      appliedFilters.cities.length,
      appliedFilters.sectors.length,
      appliedFilters.turnovers.length,
    ]
  );

  // Clear restricted filters when membership changes
  useEffect(() => {
    if (membershipType === "youth") {
      clearAllFilters();
    } else if (membershipType === "business") {
      setAppliedFilters((prev) => ({
        ...prev,
        turnovers: [],
      }));
    }
  }, [membershipType, clearAllFilters]);

  /* ================= HELPER FUNCTIONS ================= */

  const mergeImages = useCallback((items: User[]) => {
    if (!items) return [];
    return items.map((u) => ({
      ...u,
      ...fullUsersImages.find((i) => i.userId === u.id)?.imageUrls,
    }));
  }, [fullUsersImages]);

  const sortUsers = useCallback((items: User[] = []) => {
    if (!items) return [];
    return [...items].sort((a, b) => {
      if (
        sortOption === "connections.sort.ascending" ||
        sortOption === "connections.sort.descending"
      ) {
        const aName = `${a.firstName || ""} ${a.lastName || ""}`.trim();
        const bName = `${b.firstName || ""} ${b.lastName || ""}`.trim();
        const cmp = aName.localeCompare(bName, undefined, {
          sensitivity: "base",
        });
        return sortOption === "connections.sort.descending" ? -cmp : cmp;
      }
      return 0;
    });
  }, [sortOption]);

  const filterExcludedUsers = useCallback((items: User[] = []) => {
    if (!items) return [];
    return items.filter(
      (u, i, self) =>
        i === self.findIndex((x) => x.objectId === u.objectId) &&
        u.objectId !== userId &&
        !connections?.userDtos?.some((c) => c.objectId === u.objectId) &&
        !connectionRequests?.userDtos?.some((r) => r.objectId === u.objectId) &&
        // Explorers only discover other Explorers (hide paid Early Stage / members).
        (!isExplorer || isUnpaidExplorerUser(u))
    );
  }, [userId, connections?.userDtos, connectionRequests?.userDtos, isExplorer]);

  const activeDiscoverFilterOpts = useMemo(() => {
    const cities =
      canUseCityFilter && appliedFilters.cities.length > 0
        ? appliedFilters.cities
        : [];
    const sectors =
      canUseSectorFilter && appliedFilters.sectors.length > 0
        ? appliedFilters.sectors
        : [];
    const turnovers =
      canUseTurnoverFilter && appliedFilters.turnovers.length > 0
        ? appliedFilters.turnovers
        : [];
    return { cities, sectors, turnovers };
  }, [
    canUseCityFilter,
    canUseSectorFilter,
    canUseTurnoverFilter,
    appliedFilters.cities,
    appliedFilters.sectors,
    appliedFilters.turnovers,
  ]);

  const applyDiscoverFilters = useCallback(
    (items: User[] = []) => {
      const { cities, sectors, turnovers } = activeDiscoverFilterOpts;
      if (
        cities.length === 0 &&
        sectors.length === 0 &&
        turnovers.length === 0
      ) {
        return items;
      }
      return items.filter((u) =>
        userMatchesDiscoverFilters(u, {
          cities,
          sectors,
          turnovers,
        }),
      );
    },
    [activeDiscoverFilterOpts],
  );

  const filterUsersBySearch = useCallback((items: User[] = [], term: string) => {
    if (!items.length || !term.trim()) return items;
    const q = term.trim().toLowerCase();
    const tokens = q.split(/\s+/).filter(Boolean);

    const matchesHaystack = (haystack: string) => {
      if (!haystack) return false;
      if (haystack.includes(q)) return true;
      if (tokens.length > 1) {
        return tokens.every((token) => haystack.includes(token));
      }
      return false;
    };

    return items.filter((u) => {
      const first = (u.firstName || "").toLowerCase();
      const last = (u.lastName || "").toLowerCase();
      const full = `${first} ${last}`.trim();
      const business = (u.businessName || "").toLowerCase();
      const sector = (u.businessSectorIndustry || "").toLowerCase();
      const city = (u.city || "").toLowerCase();
      const country = (u.country || "").toLowerCase();
      const businessCountry = (
        u.businessCountryOperation ||
        u.businessCountry ||
        ""
      ).toLowerCase();
      const location = `${city} ${country} ${businessCountry}`.trim();

      if (
        matchesHaystack(first) ||
        matchesHaystack(last) ||
        matchesHaystack(full)
      ) {
        return true;
      }
      if (
        matchesHaystack(business) ||
        matchesHaystack(sector) ||
        matchesHaystack(location)
      ) {
        return true;
      }
      // Multi-token across fields: "John Smith", "London Tech", etc.
      if (tokens.length > 1) {
        return tokens.every(
          (token) =>
            first.includes(token) ||
            last.includes(token) ||
            full.includes(token) ||
            business.includes(token) ||
            sector.includes(token) ||
            city.includes(token) ||
            country.includes(token) ||
            businessCountry.includes(token),
        );
      }
      return false;
    });
  }, []);

  const users = useMemo(() => {
    return filterUsersBySearch(
      applyDiscoverFilters(
        filterExcludedUsers(sortUsers(mergeImages(fullUsersList))),
      ),
      debouncedSearchTerm,
    );
  }, [
    fullUsersList,
    mergeImages,
    sortUsers,
    filterExcludedUsers,
    applyDiscoverFilters,
    filterUsersBySearch,
    debouncedSearchTerm,
  ]);

  const connectionsListData = useMemo(
    () => mergeImages(sortUsers(connections?.userDtos ?? [])),
    [connections?.userDtos, mergeImages, sortUsers]
  );

  const requestsListData = useMemo(
    () => mergeImages(sortUsers(connectionRequests?.userDtos ?? [])),
    [connectionRequests?.userDtos, mergeImages, sortUsers]
  );

  const sentRequestsListData = useMemo(
    () => mergeImages(sortUsers(sentConnectionRequests?.userDtos ?? [])),
    [sentConnectionRequests?.userDtos, mergeImages, sortUsers]
  );

  const filteredConnectionsListData = useMemo(
    () =>
      filterUsersBySearch(
        applyDiscoverFilters(connectionsListData),
        debouncedSearchTerm,
      ),
    [
      connectionsListData,
      debouncedSearchTerm,
      filterUsersBySearch,
      applyDiscoverFilters,
    ],
  );

  const filteredRequestsListData = useMemo(
    () =>
      filterUsersBySearch(
        applyDiscoverFilters(requestsListData),
        debouncedSearchTerm,
      ),
    [
      requestsListData,
      debouncedSearchTerm,
      filterUsersBySearch,
      applyDiscoverFilters,
    ],
  );

  const filteredSentRequestsListData = useMemo(
    () =>
      filterUsersBySearch(
        applyDiscoverFilters(sentRequestsListData),
        debouncedSearchTerm,
      ),
    [
      sentRequestsListData,
      debouncedSearchTerm,
      filterUsersBySearch,
      applyDiscoverFilters,
    ],
  );

  useEffect(() => {
    if (!isFetchingUsers) loadMoreLockRef.current = false;
  }, [isFetchingUsers]);

  const loadMoreUsers = useCallback(() => {
    if (loadMoreLockRef.current || isFetchingUsers || isUsersError || !hasMoreUsers) {
      return;
    }
    loadMoreLockRef.current = true;
    setCurrentPage((p) => p + 1);
  }, [isFetchingUsers, isUsersError, hasMoreUsers]);

  const handleOpenFilters = useCallback(() => setShowFilters(true), []);
  const handleCloseFilters = useCallback(() => setShowFilters(false), []);
  const handleTabSelect = useCallback(
    (k: string | null) => k && setActiveTab(k as "connections" | "requests" | "users"),
    []
  );
  const handleSortSelect = useCallback((k: string | null) => {
    if (k) setSortOption(k);
  }, []);

  /* ================= UI ================= */

  return (
    <div className="connections-root position-relative pb-4 d-flex flex-column min-vh-100 vh-100 overflow-hidden min-vw-0">
      {/* Header: sticky on mobile (via CSS), fixed at top on desktop (scroll area below) */}
      <div className="connections-header-sticky bg-white pb-2 flex-shrink-0 sticky-top top-0 z-1 min-vw-0 position-md-static">
      {/* Desktop: Header Row */}
      <div className="d-none d-md-flex flex-row justify-content-between align-items-center mb-3">
        <h5 className="fw-bold mb-0 text-dark">{t("connections.title")}</h5>
      </div>

      {/* Mobile: Subtitle row + title – keep equal height across tabs */}
      <div className="d-md-none mb-3">
        {activeTab === "users" && (
          <div className="d-flex flex-row justify-content-between align-items-baseline w-100 mb-1 gap-2 min-w-0">
            <span className="text-muted small text-nowrap connections-mobile-subtitle invisible">{t("connections.incomingRequests")}</span>
            <span className="text-muted text-end small text-nowrap connections-mobile-subtitle invisible">
              {t("connections.acceptToAdd")}
            </span>
          </div>
        )}
        {activeTab === "requests" && (
          <div className="d-flex flex-row justify-content-between align-items-baseline w-100 mb-1 gap-2 min-w-0">
            <span className="text-muted small text-nowrap connections-mobile-subtitle">{t("connections.incomingRequests")}</span>
            <span className="text-muted text-end small text-nowrap connections-mobile-subtitle">{t("connections.acceptToAdd")}</span>
          </div>
        )}
        {activeTab === "connections" && (
          <div className="d-flex flex-row justify-content-between align-items-baseline w-100 mb-1">
            <span className="text-muted small connections-mobile-subtitle">{t("connections.yourConnections")}</span>
            <span className="text-muted text-end small connections-mobile-subtitle">{t("connections.connectedCount", { count: filteredConnectionsListData.length })}</span>
          </div>
        )}
        <h5 className="fw-bold mb-0 text-dark">{t("connections.title")}</h5>
      </div>

      <ExplorerConnectionLimitCounter className="mb-3" />

      {/* FILTER SIDEBAR / MODAL: offcanvas on desktop, full-screen modal on mobile */}
      <Offcanvas
        show={showFilters}
        onHide={handleCloseFilters}
        placement="end"
        style={{
          top: "calc(80px + 0.5rem)",
          height: "calc(100vh - 80px - 0.5rem)",
        }}
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>{t("connections.filtersTitle")}</Offcanvas.Title>
        </Offcanvas.Header>

        <Offcanvas.Body>
          {/* City filter — same worldwide city search as registration */}
          <div className="mb-3">
            <label className="form-label small fw-bold">{t("connections.city")}</label>
            <AsyncSelect
              cacheOptions
              defaultOptions={false}
              isClearable={false}
              isDisabled={!canUseCityFilter}
              isSearchable
              filterOption={null}
              placeholder={t("registration.personalInfo.placeholder.city")}
              loadOptions={(inputValue) => loadCityOptions(inputValue)}
              onChange={(opt: CityOption | null) => {
                if (!opt || !canUseCityFilter) return;
                const label = formatCityCountryLabel(opt.city, opt.country);
                if (label) addFilter("cities", label);
              }}
              value={null}
              noOptionsMessage={({ inputValue }) => {
                if (!inputValue || inputValue.trim().length === 0) {
                  return t("registration.personalInfo.placeholder.city");
                }
                return t("connections.noCitiesAvailable");
              }}
              loadingMessage={() => t("connections.loading")}
              classNamePrefix="connections-city-select"
              styles={{
                control: (base) => ({
                  ...base,
                  minHeight: 31,
                  fontSize: "0.875rem",
                }),
                valueContainer: (base) => ({
                  ...base,
                  paddingTop: 0,
                  paddingBottom: 0,
                }),
                indicatorsContainer: (base) => ({
                  ...base,
                  height: 31,
                }),
              }}
            />
            {!canUseCityFilter && (
              <div className="text-muted small mt-1">
                {t(getUpgradeMessageKey(membershipType, "city"))}
              </div>
            )}
            {appliedFilters.cities.length > 0 && (
              <div className="d-flex flex-wrap gap-2 mt-2">
                {appliedFilters.cities.map((city, i) => (
                  <span key={i} className="badge bg-primary">
                    {city}
                    <button
                      type="button"
                      className="btn-close btn-close-white ms-2"
                      onClick={() => removeFilter("cities", i)}
                      aria-label={t("connections.remove")}
                    />
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Sector Filter */}
          <div className="mb-3">
            <label className="form-label small fw-bold">{t("connections.sectorIndustry")}</label>
            <Form.Select
              size="sm"
              value={sectorFilter}
              onChange={(e) => {
                if (e.target.value && canUseSectorFilter) {
                  addFilter("sectors", e.target.value);
                  setSectorFilter("");
                }
              }}
              disabled={!canUseSectorFilter}
            >
              <option value="">{t("connections.selectSector")}</option>
              {SECTOR_FILTER_OPTIONS.map((s) => (
                <option key={s.value} value={s.value}>
                  {t(s.labelKey)}
                </option>
              ))}
            </Form.Select>
            {!canUseSectorFilter && (
              <div className="text-muted small mt-1">
                {t(getUpgradeMessageKey(membershipType, "sector"))}
              </div>
            )}
            {appliedFilters.sectors.length > 0 && (
              <div className="d-flex flex-wrap gap-2 mt-2">
                {appliedFilters.sectors.map((sector, i) => (
                  <span key={i} className="badge bg-primary">
                    {labelForSectorValue(sector)}
                    <button
                      type="button"
                      className="btn-close btn-close-white ms-2"
                      onClick={() => removeFilter("sectors", i)}
                      aria-label={t("connections.remove")}
                    />
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Turnover Filter */}
          <div className="mb-3">
            <label className="form-label small fw-bold">
              {t("connections.businessTurnover")} (GBP)
            </label>
            <Form.Select
              size="sm"
              value={turnoverFilter}
              onChange={(e) => {
                if (e.target.value && canUseTurnoverFilter) {
                  addFilter("turnovers", e.target.value);
                  setTurnoverFilter("");
                }
              }}
              disabled={!canUseTurnoverFilter}
            >
              <option value="">{t("connections.selectTurnover")}</option>
              {TURNOVER_FILTER_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {t(opt.labelKey)}
                </option>
              ))}
            </Form.Select>
            {!canUseTurnoverFilter && (
              <div className="text-muted small mt-1">
                {t(getUpgradeMessageKey(membershipType, "turnover"))}
              </div>
            )}
            {appliedFilters.turnovers.length > 0 && (
              <div className="d-flex flex-wrap gap-2 mt-2">
                {appliedFilters.turnovers.map((turnover, i) => (
                  <span key={i} className="badge bg-primary">
                    {labelForTurnoverValue(turnover)}
                    <button
                      type="button"
                      className="btn-close btn-close-white ms-2"
                      onClick={() => removeFilter("turnovers", i)}
                      aria-label={t("connections.remove")}
                    />
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Clear All Filters */}
          {hasActiveFilters && (
            <Button
              variant="outline-danger"
              size="sm"
              className="w-100 mb-3"
              onClick={clearAllFilters}
            >
              {t("connections.clearAllFilters")}
            </Button>
          )}

          <Button
            variant="primary"
            className="w-100"
            onClick={handleCloseFilters}
          >
            {t("connections.applyFilters")}
          </Button>
        </Offcanvas.Body>
      </Offcanvas>

      {/* Tablet (768–991px): stacked – tabs full width, then search+sort. Desktop (992px+): tabs left, search+sort right */}
      <div className="d-none d-md-flex row align-items-end g-2 mb-1" style={{ direction: "ltr" }}>
        {/* Tabs: full width on tablet only */}
        <div className="col-12 col-lg-auto">
          <div className="bg-light rounded-2 border shadow-sm d-flex p-1 w-100">
          <Nav
            activeKey={activeTab}
            onSelect={handleTabSelect}
            className="d-flex flex-nowrap gap-2 w-100"
          >
            <Nav.Item className="flex-fill flex-lg-grow-0">
              <Nav.Link
                eventKey="connections"
                className={`d-flex align-items-center gap-2 rounded-2 text-dark text-decoration-none border-0 px-2 py-1 ${activeTab === "connections" ? "shadow bg-white" : "bg-transparent"}`}
                title={t("connections.peopleConnectedWith")}
              >
                <BsPeople size={16} className={`flex-shrink-0 ${activeTab === "connections" ? "text-primary" : "text-dark"}`} aria-hidden />
                <span className="d-flex flex-column align-items-start text-start small lh-sm">
                  <span className={activeTab === "connections" ? "text-primary fw-bold" : "text-dark fw-normal"}>{t("connections.myNetwork")}</span>
                  <span className="text-muted small">{t("connections.yourNetwork")}</span>
                </span>
              </Nav.Link>
            </Nav.Item>
            <Nav.Item className="flex-fill flex-lg-grow-0">
              <Nav.Link
                eventKey="users"
                className={`d-flex align-items-center gap-2 rounded-2 text-dark text-decoration-none border-0 px-2 py-1 ${activeTab === "users" ? "shadow bg-white" : "bg-transparent"}`}
                title={t("connections.discoverTabTitle")}
              >
                <BsSearch size={16} className={`flex-shrink-0 ${activeTab === "users" ? "text-primary" : "text-dark"}`} aria-hidden />
                <span className="d-flex flex-column align-items-start text-start small lh-sm">
                  <span className={activeTab === "users" ? "text-primary fw-bold" : "text-dark fw-normal"}>{t("connections.discover")}</span>
                  <span className="text-muted small">{t("connections.discoverPeople")}</span>
                </span>
              </Nav.Link>
            </Nav.Item>
            <Nav.Item className="flex-fill flex-lg-grow-0">
              <Nav.Link
                eventKey="requests"
                className={`d-flex align-items-center gap-2 rounded-2 text-dark text-decoration-none border-0 px-2 py-1 ${activeTab === "requests" ? "shadow bg-white" : "bg-transparent"}`}
                title={t("connections.pendingRequestsTitle")}
              >
                <BsEnvelopeArrowDown size={16} className={`flex-shrink-0 ${activeTab === "requests" ? "text-primary" : "text-dark"}`} aria-hidden />
                <span className="d-flex flex-column align-items-start text-start small lh-sm">
                  <span className={`d-inline-flex align-items-center gap-1 ${activeTab === "requests" ? "text-primary fw-bold" : "text-dark fw-normal"}`}>
                    {t("connections.requests")}
                    {requestsListData.length > 0 && (
                      <Badge bg="primary" pill className="small px-2">{requestsListData.length}</Badge>
                    )}
                  </span>
                  <span className="text-muted small">{t("connections.pendingInvites")}</span>
                </span>
              </Nav.Link>
            </Nav.Item>
          </Nav>
          </div>
        </div>

        {/* Search + Sort: full width on tablet, right-aligned on desktop */}
        <div className="col-12 col-lg d-flex align-items-center gap-1 justify-content-lg-end min-w-0 w-100">
          <InputGroup
            className="rounded-2 flex-grow-1 w-100 flex-lg-grow-0 min-w-0"
            style={isLgUp ? { maxWidth: 400 } : undefined}
          >
            <InputGroup.Text className="bg-white border border-end-0 rounded-start d-flex align-items-center px-1 px-md-2">
              <BsSearch size={16} className="text-secondary" aria-hidden />
            </InputGroup.Text>
            <Form.Control
              placeholder={t("connections.searchPlaceholder")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              aria-label={t("connections.search")}
              className="border border-start-0 rounded-end bg-white shadow-none min-w-0"
            />
          </InputGroup>
          <Button
            variant="outline-primary"
            className="d-flex align-items-center gap-2 position-relative rounded-2 shadow-none flex-shrink-0 px-2"
            onClick={handleOpenFilters}
            aria-label={t("connections.filter")}
          >
            <BsFilter size={16} aria-hidden />
            <span>{t("connections.filter")}</span>
            {hasActiveFilters && (
              <Badge bg="primary" pill className="position-absolute top-0 start-100 translate-middle">
                {appliedFilters.cities.length +
                  appliedFilters.sectors.length +
                  appliedFilters.turnovers.length}
              </Badge>
            )}
          </Button>
          <Dropdown onSelect={handleSortSelect}>
            <Dropdown.Toggle
              variant="outline-primary"
              className="d-flex align-items-center gap-2 rounded-2 shadow-none flex-shrink-0 px-2"
              aria-label={t("connections.sortBy")}
            >
              <BsSortDown size={16} aria-hidden />
              <span>{t("connections.sort")}</span>
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item eventKey="connections.sort.recent">{t("connections.sort.recent")}</Dropdown.Item>
              <Dropdown.Item eventKey="connections.sort.ascending">{t("connections.sort.ascending")}</Dropdown.Item>
              <Dropdown.Item eventKey="connections.sort.descending">{t("connections.sort.descending")}</Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>

      {/* Mobile: Navigation Tabs – compact size, rounded container, icon above text */}
      <nav className="d-sm-none mb-2 position-relative" aria-label={t("connections.title")}>
        <div className="bg-light rounded-2 border shadow-sm d-flex p-1 overflow-x-auto">
          <button
            type="button"
            className={`d-flex flex-column align-items-center justify-content-center gap-1 border-0 flex-fill py-1 px-0 rounded-1 ${activeTab === "connections" ? "bg-primary text-white fw-semibold" : "bg-white text-dark"}`}
            onClick={() => setActiveTab("connections")}
            aria-label={t("connections.networkMobileAria")}
          >
            <BsPeople size={14} aria-hidden />
            <span className="small">{t("connections.myNetwork")}</span>
          </button>
          <button
            type="button"
            className={`d-flex flex-column align-items-center justify-content-center gap-1 border-0 flex-fill py-1 px-0 rounded-1 ${activeTab === "users" ? "bg-primary text-white fw-semibold" : "bg-white text-dark"}`}
            onClick={() => setActiveTab("users")}
            aria-label={t("connections.discoverMobileAria")}
          >
            <BsSearch size={14} aria-hidden />
            <span className="small">{t("connections.discover")}</span>
          </button>
          <button
            type="button"
            className={`d-flex flex-column align-items-center justify-content-center gap-1 border-0 flex-fill position-relative py-1 px-0 rounded-1 ${activeTab === "requests" ? "bg-primary text-white fw-semibold" : "bg-white text-dark"}`}
            onClick={() => setActiveTab("requests")}
            aria-label={t("connections.requestsMobileAria")}
          >
            <BsEnvelopeArrowDown size={14} aria-hidden />
            <span className="small">{t("connections.requests")}</span>
          </button>
        </div>
      </nav>

      {/* Mobile: Search bar – full placeholder visible like reference image */}
      <div className="d-flex d-md-none flex-column gap-2 mb-0">
        <InputGroup className="rounded-3 border shadow-sm w-100 bg-white min-w-0">
          <Form.Control
            placeholder={t("connections.searchPlaceholder")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            aria-label={t("connections.searchPlaceholder")}
            className="border-0 border-end rounded-start bg-white shadow-none py-2 px-3 flex-grow-1 min-w-0 connections-search-placeholder"
          />
          <InputGroup.Text className="bg-white border-0 border-start rounded-end d-flex align-items-center px-2 py-2 flex-shrink-0">
            <BsSearch size={16} className="text-secondary" aria-hidden />
          </InputGroup.Text>
        </InputGroup>
      </div>
      </div>
      {/* END FIXED – scrollable section below */}

      <div
        id={CONNECTIONS_SCROLL_AREA_ID}
        ref={scrollAreaRef}
        className="connections-scroll-area d-flex flex-column pt-2 pt-md-0 overflow-y-auto overflow-x-hidden flex-grow-1 min-h-0 min-vw-0"
      >
        <div className="d-flex d-md-none align-items-stretch justify-content-end gap-2 w-100 mb-2" style={{ direction: "ltr" }}>
          <Button
            variant="outline-primary"
            className="d-flex align-items-center gap-2 position-relative rounded-2 text-nowrap shadow-none py-2 px-3"
            onClick={handleOpenFilters}
            aria-label={t("connections.filter")}
          >
            <BsFilter size={16} aria-hidden />
            <span>{t("connections.filters")}</span>
            {hasActiveFilters && (
              <Badge bg="primary" pill className="position-absolute top-0 start-100 translate-middle">
                {appliedFilters.cities.length +
                  appliedFilters.sectors.length +
                  appliedFilters.turnovers.length}
              </Badge>
            )}
          </Button>
          <Dropdown onSelect={handleSortSelect}>
            <Dropdown.Toggle
              variant="outline-primary"
              className="d-flex align-items-center gap-2 rounded-2 shadow-none py-2 px-3"
              aria-label={t("connections.sort")}
            >
              <BsSortDown size={16} aria-hidden />
              <span>{t("connections.sort")}</span>
            </Dropdown.Toggle>
            <Dropdown.Menu popperConfig={{ strategy: "fixed" }}>
              <Dropdown.Item eventKey="connections.sort.recent">{t("connections.sort.recent")}</Dropdown.Item>
              <Dropdown.Item eventKey="connections.sort.ascending">{t("connections.sort.ascending")}</Dropdown.Item>
              <Dropdown.Item eventKey="connections.sort.descending">{t("connections.sort.descending")}</Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>

      {/* Subtitle row: same structure on all tabs so cards start at same position (Bootstrap invisible on Discover) */}
      <div className="d-none d-sm-flex justify-content-between align-items-center mb-1 gap-2 min-w-0">
        {activeTab === "users" && (
          <>
            <h6 className="mb-0 fw-normal text-nowrap connections-mobile-subtitle invisible">{t("connections.incomingRequests")}</h6>
            <span className="text-muted small mb-0 mb-md-2 mt-0 text-nowrap text-end connections-mobile-subtitle invisible">{t("connections.acceptToAdd")}</span>
          </>
        )}
        {activeTab === "requests" && (
          <>
            <h6 className="mb-0 fw-normal text-nowrap connections-mobile-subtitle">{t("connections.incomingRequests")}</h6>
            <span className="text-muted small mb-0 mb-md-2 mt-0 text-nowrap text-end connections-mobile-subtitle">{t("connections.acceptToAdd")}</span>
          </>
        )}
        {activeTab === "connections" && (
          <>
            <h6 className="mb-0 fw-normal connections-mobile-subtitle">{t("connections.yourConnections")}</h6>
            <span className="text-muted small mb-0 mb-md-2 mt-0 connections-mobile-subtitle connections-subtitle">
              {t("connections.connectedCount", { count: filteredConnectionsListData.length })}
            </span>
          </>
        )}
      </div>

      <div className="mt-1 mt-md-2 pb-5 pb-md-0">
        {activeTab === "users" && (
          <Suspense fallback={<div className="min-vh-50" aria-hidden />}>
            <UsersList
              users={users}
              // Unfiltered API accumulation — sentinel re-checks after each merged page
              // even when client filters hide most of the page.
              scrollDataLength={fullUsersList.length}
              loadMoreUsers={loadMoreUsers}
              hasMoreUsers={hasMoreUsers}
              isLoadingMore={isFetchingUsers}
              currentConnectionSource={currentConnectionSource}
              explorerLimitReached={explorerLimitReached}
              onBlockedConnectionAction={guardConnectionAction}
              onConnectionLimitError={handleConnectionLimitError}
            />
          </Suspense>
        )}

        {activeTab === "connections" && (
          <Suspense fallback={<div className="min-vh-50" aria-hidden />}>
            <ConnectionsList
              connections={filteredConnectionsListData}
              refetchConnections={refetchConnections}
            />
          </Suspense>
        )}

        {activeTab === "requests" && (
          <Suspense fallback={<div className="min-vh-50" aria-hidden />}>
            <RequestsList
              requests={filteredRequestsListData}
              sentRequests={filteredSentRequestsListData}
              refetchConnections={refetchConnections}
              refetchConnectionRequests={refetchConnectionRequests}
              refetchSentConnectionRequests={refetchSentConnectionRequests}
              explorerLimitReached={explorerLimitReached}
              onBlockedConnectionAction={guardConnectionAction}
              onConnectionLimitError={handleConnectionLimitError}
            />
          </Suspense>
        )}
      </div>
      </div>
      {limitModal}
    </div>
  );
};

export default Connections;